import { Divider, Radio, Stack, Tooltip, Typography } from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import {
  type InventoryResource,
  type SecurityScanResult,
  type Snapshot,
} from '@repo/api-gw-sdk';
import classnames from 'classnames';

import {
  getEvidenceStrings,
  hasSecurityIssue,
} from '@/components/security/securityUtils';
import { SkullIcon } from '@/components/security/skullIcon';
import { Icon } from '@/components/shared/icon';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { RetentionTimeline } from '@/components/snapshots/convertToEon/retentionTimeline';
import { ShieldIcon } from '@/components/snapshots/shieldIcon';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import type { BackupVault } from '@/data/vaults/backupVault';
import { fromDaysToBiggestAsPossible, isDateValid } from '@/utils/dateTime';
import { DATE_FORMAT, dayjs, TIME_FORMAT } from '@/utils/dayjs';

interface SnapshotDetailsHeaderProps {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  snapshotSelection: boolean;
  resource?: InventoryResource;
}

export const SnapshotDetailsHeader = ({
  snap,
  isSelected,
  onSelect,
  vault,
  snapshotSelection,
  resource,
}: SnapshotDetailsHeaderProps) => {
  const isNativeSnapshot = snap.isNative ?? false;
  const dal = useDAL();
  const { isEmployee } = useUser();
  const { isDemo } = useEnvironment();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const showSnapshotId =
    isEmployee && !isDemo && prefResponse?.value?.isDevMode;

  const date = isDateValid(snap.pointInTime)
    ? snap.pointInTime
    : snap.createdTime;

  const showPartial = snap.accessDeniedItemsCount || snap.skippedItemsCount;

  return (
    <Stack
      className={classnames('flex-1', { 'cursor-pointer': snapshotSelection })}
      direction='row'
      gap='40px'
      alignItems='center'
      onClick={() => onSelect()}
      sx={{
        '&:hover': {
          '& .retention-info': {
            visibility: 'visible',
          },
        },
      }}
    >
      <Stack direction='row' gap='60px' alignItems='center'>
        <Stack width='238px' direction='row' gap='36px' alignItems='center'>
          <Stack direction='row' gap='12px' alignItems='center'>
            {snapshotSelection && <Radio size='small' checked={isSelected} />}
            {(vault || isNativeSnapshot) && (
              <SnapshotIndicator
                sx={{
                  margin: '6px',
                }}
                backgroundColor={vault?.backgroundColor || null}
                isNative={isNativeSnapshot}
                isPartial={!!snap.accessDeniedItemsCount}
              />
            )}
            <Stack direction='row' alignItems='center' gap='4px'>
              <Stack>
                <Typography width='100px' variant='subtitle1'>
                  {`${dayjs.utc(date).format(TIME_FORMAT)} UTC`}
                </Typography>
                {showSnapshotId && <Typography>{snap.id}</Typography>}
              </Stack>
              {snap.onHold ? (
                <HoldIndicator snap={snap} vault={vault} />
              ) : snap.protectedFromDeletion ? (
                <RansomwareProtectedIndication snap={snap} vault={vault} />
              ) : (
                <RetentionIndicator snapshot={snap} vault={vault} />
              )}
            </Stack>
          </Stack>
          <Stack direction={'row'} gap='4px' alignItems='center'>
            {hasSecurityIssue(snap) && <RansomwareIndication snap={snap} />}
            {showPartial && <PartialInfoIndication snap={snap} />}
          </Stack>
        </Stack>
        {isNativeSnapshot && resource ? (
          <Stack direction='row' gap='12px' alignItems='center'>
            <Typography variant='subtitle1'>Stored in</Typography>
            <AccountAsVaultTag
              cloudProvider={resource.cloudProvider}
              region={resource.region}
            />
          </Stack>
        ) : (
          vault && (
            <Stack direction='row' gap='12px' alignItems='center'>
              <Typography variant='subtitle1'>Stored in</Typography>
              <VaultTag vault={vault} />
            </Stack>
          )
        )}
      </Stack>
    </Stack>
  );
};

const HoldIndicator = ({
  snap,
  vault,
}: {
  snap: Snapshot;
  vault: BackupVault | undefined;
}) => {
  const description =
    'On hold by user request. When the hold is removed, the original expiration will apply.';
  return (
    <RetentionTooltip snapshot={snap} vault={vault} description={description}>
      <Icon iconClass='material-symbols-all-inclusive' />
    </RetentionTooltip>
  );
};

const RetentionTooltip = ({
  snapshot,
  vault,
  description,
  children,
}: {
  snapshot: Snapshot;
  vault: BackupVault | undefined;
  description?: string;
  children: TooltipProps['children'];
}) => {
  const retention = snapshot.backupRetention;
  const expirationDate = snapshot.expirationTime;
  const pointDate = snapshot.pointInTime;

  if (
    typeof retention === 'number' &&
    retention > 0 &&
    isDateValid(expirationDate) &&
    isDateValid(pointDate)
  ) {
    const retentionDuration = fromDaysToBiggestAsPossible(retention);
    const expired =
      isDateValid(expirationDate) &&
      dayjs.utc(expirationDate).isBefore(dayjs.utc());

    return (
      <Tooltip
        disableInteractive={false}
        title={
          <Stack
            paddingX='16px'
            width='100%'
            gap='24px'
            divider={<Divider color='var(--mui-palette-divider)' />}
          >
            {description && (
              <Typography color='var(--mui-palette-Tooltip-text)'>
                {description}
              </Typography>
            )}
            <Stack gap='12px'>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
              >
                <Typography color='var(--mui-palette-Tooltip-text)'>
                  Retention
                </Typography>
                <Typography color='var(--mui-palette-Tooltip-text)'>
                  {`${retentionDuration.value} ${retentionDuration.unit}${retentionDuration.unit !== 'days' ? ` (${retention} days)` : ''}`}
                </Typography>
              </Stack>
              <RetentionTimeline
                color={vault?.backgroundColor}
                start={pointDate}
                end={expirationDate}
                elapsed={new Date()}
              />
              <Typography
                color='var(--mui-palette-Tooltip-text)'
                alignSelf='end'
              >
                {`${expired ? 'Expired' : 'Expires'} on ${dayjs.utc(expirationDate).format(DATE_FORMAT)}`}
              </Typography>
            </Stack>
          </Stack>
        }
        slotProps={{
          tooltip: {
            sx: {
              width: '280px',
              maxWidth: '280px',
              boxShadow: 'var(--mui-customShadows-panel)',
              padding: '16px 0',
              borderRadius: '4px',
            },
          },
        }}
      >
        {children}
      </Tooltip>
    );
  }

  return null;
};

const RetentionIndicator = ({
  snapshot,
  vault,
}: {
  snapshot: Snapshot;
  vault: BackupVault | undefined;
}) => {
  return (
    <RetentionTooltip snapshot={snapshot} vault={vault}>
      <Icon
        className='retention-info'
        iconClass='material-symbols-hourglass-arrow-down-outline-rounded'
        sx={{
          visibility: 'hidden',
        }}
      />
    </RetentionTooltip>
  );
};

const PartialInfoIndication = ({ snap }: { snap: Snapshot }) => {
  let title = '';
  if (snap.accessDeniedItemsCount) {
    title =
      snap.accessDeniedItemsCount === 1
        ? 'This is a partial snapshot. 1 file wasn`t backed up.'
        : `This is a partial snapshot. ${snap.accessDeniedItemsCount} files weren’t backed up.`;
  } else if (snap.skippedItemsCount) {
    title = `This Point-in-time includes ${snap.skippedItemsCount} fewer logical databases than the previous full snapshot. This may happen if databases were deleted or if there was an issue during backup.`;
  }

  return (
    <Tooltip
      title={title}
      slotProps={{
        tooltip: {
          sx: {
            padding: '16px',
          },
        },
      }}
    >
      <Icon iconClass='material-symbols-info-outline' />
    </Tooltip>
  );
};

const RansomwareIndication = ({ snap }: { snap: Snapshot }) => {
  const evidences: SecurityScanResult[] = snap.securityScan?.results || [];
  const description = `This snapshot is infected with ransomware.${evidences.length ? ' Suspicious indicators found:' : ''}`;

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '240px',
          },
        },
      }}
      title={
        <Stack padding='4px' gap='12px'>
          <Typography color='var(--mui-palette-Tooltip-text)'>
            {description}
          </Typography>
          <Stack gap='4px'>
            {evidences.map((evidence) => (
              <Stack
                key={evidence.evidenceType}
                direction='row'
                justifyContent='space-between'
              >
                <Typography color='var(--mui-palette-Tooltip-text)'>
                  {getEvidenceStrings(evidence.evidenceType).label}
                </Typography>
                <Typography color='var(--mui-palette-Tooltip-text)'>
                  {evidence.count}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      }
    >
      <span className='h-[20px] mt-[-4px]'>
        <SkullIcon />
      </span>
    </Tooltip>
  );
};

const RansomwareProtectedIndication = ({
  snap,
  vault,
}: {
  snap: Snapshot;
  vault: BackupVault | undefined;
}) => {
  const description =
    'Protected from expiration because all following snapshots contain ransomware. When a new clean snapshot is taken, protection is lifted and the original expiration will apply.';
  return (
    <RetentionTooltip snapshot={snap} vault={vault} description={description}>
      <span>
        <ShieldIcon />
      </span>
    </RetentionTooltip>
  );
};
